// components/modules/assign/assign-component.jsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { FaUserSlash } from "react-icons/fa";
import { db, assignMember, unAssign } from "../../../firebase/firebase.utils";
import Avatar from "../../retro/avatar/avatar.component";
import BoxLayerLite from "../../retro/box-layer-lite/box-layer-lite.component";
import Tooltip from "../../retro/tooltip/tooltip.component";
import "./assign-styles.scss";

const EMPTY = [];

export default function Assign({ task }) {
  const users = useSelector((state) => state.user.users) || EMPTY;
  const spaces = useSelector((state) => state.space.spaceData) || EMPTY;
  const currentUser = useSelector((state) => state.user.currentUser);
  const navigate = useNavigate();
  const spaceId = task?.fromSpaceId;
  const stationId = task?.fromStationId;
  const isQuick = spaceId === `${currentUser?.uid}-Inbox`;
  // habit related start
  const isHabitReminder = task?.type === "habitReminder";
  const assignmentLocked = isQuick || isHabitReminder;
  // habit related end
  const workspace = spaces.find((space) => space.spaceId === spaceId);
  const workspaceMembers = workspace?.members || EMPTY;
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const assignedUser = assignmentLocked
    ? currentUser
    : [...users, ...members, currentUser].find(
        (user) => user?.uid === task?.assign,
      );

  useEffect(() => {
    setShowMembers(false);
    setError("");
  }, [task?.id, spaceId, stationId]);

  useEffect(() => {
    if (!showMembers || !currentUser?.uid || assignmentLocked) {
      return;
    }

    let active = true;
    setMembers(EMPTY);
    setLoading(true);
    setError("");

    async function loadMembers() {
      try {
        if (!spaceId || !stationId) return;

        const project = await db
          .collection("space")
          .doc(spaceId)
          .collection("stations")
          .doc(stationId)
          .get();

        const memberIds = [
          ...new Set([
            ...workspaceMembers,
            ...(project.data()?.members || EMPTY),
          ]),
        ];

        const list = await Promise.all(
          memberIds.map(async (uid) => {
            const cached = users.find((user) => user.uid === uid);

            if (cached) return cached;

            const user = await db.collection("users").doc(uid).get();

            return user.exists ? { ...user.data(), uid } : null;
          }),
        );

        if (active) {
          setMembers(list.filter(Boolean));
        }
      } catch (err) {
        console.error("Could not load task members:", err);

        if (active) {
          setError("Could not load members. Please reopen the picker.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadMembers();

    return () => {
      active = false;
    };
  }, [
    showMembers,
    spaceId,
    stationId,
    assignmentLocked,
    workspaceMembers,
    users,
    currentUser,
  ]);

async function handleAssign(uid) {
  if (
    assignmentLocked ||
    !task?.id ||
    saving
  ) {
    return;
  }

  setSaving(true);
  setError("");

  try {
    if (spaceId && stationId) {
      if (uid) {
        await assignMember(
          spaceId,
          stationId,
          task.id,
          uid,
          currentUser.userName,
        );
      } else {
        await unAssign(
          spaceId,
          stationId,
          task.id,
        );
      }
    }

    setShowMembers(false);
  } catch (err) {
    console.error(
      "Could not change task assignment:",
      err,
    );

    setError(
      "Could not save the assignment. Please try again.",
    );
  } finally {
    setSaving(false);
  }
}

const assignmentTooltip =
  isHabitReminder
    ? "Habit reminders stay assigned to the habit owner."
    : isQuick
      ? "Quick Tasks are personal and stay assigned to you."
      : assignedUser?.userName || "Assign";

  return (
    <div className="assign">
<div
  className="assign__assigned"
  role={assignmentLocked ? undefined : "button"}
  tabIndex={assignmentLocked ? -1 : 0}
  aria-label={
    assignmentLocked
      ? assignmentTooltip
      : "Assign task"
  }
  aria-expanded={
    assignmentLocked
      ? undefined
      : showMembers
  }
  aria-disabled={assignmentLocked}
  onClick={() => {
    if (!assignmentLocked) {
      setShowMembers(
        !showMembers,
      );
    }
  }}
  onKeyDown={(event) => {
    if (assignmentLocked) return;

    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      setShowMembers(
        !showMembers,
      );
    }
  }}
>
  <Avatar
    user={assignedUser}
    src={assignedUser?.imageUrl || ""}
    size={27}
    tiny
    backgroundColor="unset"
    overflow="visible"
  />

  <Tooltip text={assignmentTooltip} />
</div>
      <AnimatePresence>
        {showMembers && !assignmentLocked && (
          <div className="assign__choose">
            <BoxLayerLite setLayer={setShowMembers}>
              {loading && <p role="status">Loading members…</p>}
              {error && <p role="alert">{error}</p>}
              {!loading && !error && !members.length && (
                <p>No members available.</p>
              )}
              {members.map((member) => (
                <div
                  className="assign__member"
                  key={member.uid}
                  onClick={() => handleAssign(member.uid)}
                  aria-disabled={saving}
                >
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/profile/${member.uid}`);
                    }}
                  >
                    <Avatar user={member} src={member.imageUrl} />
                  </button>
                  <p>{member.userName}</p>
                </div>
              ))}
              {task?.assign && (
                <div
                  className="assign__remove"
                  onClick={() => handleAssign(null)}
                  aria-disabled={saving}
                >
                  <FaUserSlash />
                  <p>Unassign</p>
                </div>
              )}
            </BoxLayerLite>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
